import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core'
import { CategoryNavigationComponent, CmsComponentData, NavigationService } from '@spartacus/storefront'
import { CmsNavigationComponent } from '@spartacus/core/src/model/cms.model'
import { BehaviorSubject, combineLatest, EMPTY, Observable, Subscription } from 'rxjs'
import { MbNavigationService, NavigationNode } from '../mb-navigation.service'
import { BreakpointObserver } from '@angular/cdk/layout'
import { SelectedFilter, SelectedSubCategories } from './mb-category-navigation.module'
import { filter, map, startWith, tap, withLatestFrom } from 'rxjs/operators'
import { BaseSiteService, EventService, User, WindowRef } from '@spartacus/core'
import { MbEventListenerService } from '../../services/mb-event-listener/event-listener.service'
import { ClickEvent } from '../../components/shared/gtm-event/click-event.events'
import { MediaQueries } from '../../enums/media-queries'
import { ImageSize } from '../../enums/image-size'
import { DeviceDetectorService } from 'ngx-device-detector'
import { triggerSmoothCollapseAnimation } from '../../../shared/animations/smooth-collapse.animation'
import { MbConstants } from '../../../shared/mb-constants'

@Component({
  selector: 'mb-category-navigation',
  templateUrl: './mb-category-navigation.component.html',
  styleUrls: ['./mb-category-navigation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [triggerSmoothCollapseAnimation]
})
export class MbCategoryNavigationComponent extends CategoryNavigationComponent implements OnInit, OnDestroy {

  selectedCategory$ = new BehaviorSubject<NavigationNode | undefined>(undefined)
  selectedFilter$ = new BehaviorSubject<SelectedFilter>({})
  selectedSubCategories$ = new BehaviorSubject<SelectedSubCategories>({})
  isMobileNavigationOpened$ = new BehaviorSubject(false)
  isMobile$ = new BehaviorSubject(false)

  activeFilter$: Observable<NavigationNode | undefined> = combineLatest([this.selectedFilter$, this.selectedCategory$]).pipe(
    map(([selectedFilter, category]) => selectedFilter[category?.label ?? ''] || undefined))

  activeSubCategory$ = combineLatest([this.selectedCategory$, this.activeFilter$, this.selectedSubCategories$]).pipe(
    map(([selectedCategory]) => selectedCategory?.label ?? ''),
    withLatestFrom(this.activeFilter$),
    map(([activeSubCategory, activeFilter]) => `${activeSubCategory}${!!activeFilter?.label ? `-${activeFilter.label}` : ''}`),
    withLatestFrom(this.selectedSubCategories$),
    map(([activeSubCategory, selectedSubCategories]) => selectedSubCategories[activeSubCategory]))

  isSubCategorySelected$ = combineLatest([this.selectedCategory$, this.activeFilter$, this.activeSubCategory$]).pipe(
    map(([category, activeFilter, subCategory]) => this.isSubCategorySelected(category, activeFilter, subCategory)))

  navigation$ = this.mbNavigationService.navigation$

  dataFiltered$ = this.navigation$?.pipe(
    withLatestFrom(this.selectedCategory$, this.isSubCategorySelected$),
    map(([nav, selectedCategory, isSubSelected]) => this.isDataFiltered(nav, selectedCategory, isSubSelected)))

  searchClose$ = this.eventListenerService.retrieve().pipe(
    startWith({ event: { type: MbConstants.SEARCH_CLOSE } }),
    filter(event => event?.event?.type === MbConstants.SEARCH_CLOSE),
    tap(() => this.closeSearch()))

  state$ = combineLatest([
    this.selectedCategory$,
    this.selectedSubCategories$,
    this.selectedFilter$,
    this.navigation$,
    this.searchClose$,
    this.activeFilter$,
    this.activeSubCategory$,
    this.isMobile$,
    this.isMobileNavigationOpened$,
    this.isSubCategorySelected$,
    this.dataFiltered$
  ]).pipe(map(([selectedCategory, selectedSubCategories, selectedFilter, navigation, searchClose, activeFilter, activeSubCategory,
                 isMobile, isMobileNavigationOpened, isSubCategorySelected, dataFiltered]) => ({
    selectedCategory,
    selectedSubCategories,
    selectedFilter,
    navigation,
    searchClose,
    activeFilter,
    activeSubCategory,
    isMobile,
    isMobileNavigationOpened,
    isSubCategorySelected,
    dataFiltered
  } as MbCategoryNavigationState)))

  subscription = new Subscription()
  showContact = false
  showSearch = false
  user: User | undefined
  mediaQuery = MediaQueries
  imageSize = ImageSize

  constructor (componentData: CmsComponentData<CmsNavigationComponent>,
               protected winRef: WindowRef,
               private navigationService: NavigationService,
               private mbNavigationService: MbNavigationService,
               private breakpointObserver: BreakpointObserver,
               private eventListenerService: MbEventListenerService,
               private baseSiteService: BaseSiteService,
               private cdRef: ChangeDetectorRef,
               private events: EventService,
               private deviceDetectorService: DeviceDetectorService) {
    super(componentData, navigationService)
  }

  isDataFiltered (navigation: NavigationNode[], selectedFilter: NavigationNode | undefined, isSubCategorySelected: boolean): boolean {
    if (this.selectedCategory$.value?.label === navigation[0]?.label || this.selectedCategory$.value?.label === navigation[1]?.label) {
      return !!selectedFilter && isSubCategorySelected
    }
    return isSubCategorySelected
  }

  ngOnInit (): void {
    this.subscription.add(this.eventListenerService.retrieve().subscribe(e => {
      if (e?.event?.type === 'searchClose') {
        this.closeSearch()
      }
    }))

    this.subscription.add(this.breakpointObserver.observe('(min-width: 992px)').subscribe(breakpointState => {
      const isMobile = !this.winRef.isBrowser() ? !this.deviceDetectorService.isDesktop() : !breakpointState.matches
      this.isMobile$.next(isMobile)
      this.isMobileNavigationOpened$.next(isMobile && !!this.selectedCategory$.value)
    }))
  }

  toggleSelectedCategory (category: NavigationNode, event: Event) {
    this.sendGTMEvent(event)
    this.selectedCategory$.next(this.selectedCategory$.value === category ? undefined : category)
    this.closeContact()
    this.closeSearch()
    if (!this.isMobile$.value) {
      this.subscription.add((this.navigation$ ?? EMPTY).subscribe((navigation: NavigationNode[]) => {
        if ((category?.label === MbConstants.PRODUCTS || category?.label === MbConstants.APPLICATIONS)
          && !this.selectedFilter$.value[this.selectedCategory$?.value?.label ?? '']) {
          this.toggleSelectedFilter(navigation.find(node => node.label === category.label)?.children?.[0] ?? {} as any, {} as any)
        }
      }))
    }
  }

  toggleSelectedSubCategory (event: Event, identifier: NavigationNode, subCategory: NavigationNode, additionalIdentifier?: NavigationNode) {
    this.sendGTMEvent(event)
    const label = identifier?.label + (!!additionalIdentifier?.label?.length ? '-' + (additionalIdentifier?.label ?? '') : '')
    const selectedSubCategory = this.selectedSubCategories$.value ?? {}
    selectedSubCategory[label] = subCategory === selectedSubCategory[label] ? null : subCategory
    this.selectedSubCategories$.next(selectedSubCategory)
  }

  getSubCategoryByCategoryAndFilter (): NavigationNode | null {
    const selectedFilter = this.selectedFilter$.value[this.selectedCategory$?.value?.label ?? '']
    const label = this.selectedCategory$.value?.label +
      (!!selectedFilter?.label?.length ? '-' + selectedFilter.label : '')
    return this.selectedSubCategories$.value[label]
  }

  areTeasersVisible (): boolean {
    return this.getSubCategoryByCategoryAndFilter()?.teasers?.some(teaser => teaser.isVisible) ?? false
  }

  toggleSelectedFilter (newFilter: NavigationNode, event: Event) {
    const activeFilter = this.selectedFilter$.value[this.selectedCategory$?.value?.label ?? '']
    const selectedFilter = this.selectedFilter$.value
    selectedFilter[this.selectedCategory$.value?.label ?? ''] = activeFilter === newFilter && this.isMobile$.value
      ? null
      : newFilter
    this.selectedFilter$.next(selectedFilter)
    if (event?.target) {
      this.sendGTMEvent(event)
    }
  }

  toggleMobileNavigation () {
    this.showSearch = false
    this.winRef.document.body.classList.toggle('flyout--open')
    this.isMobileNavigationOpened$.next(this.winRef.document.body.classList.contains('flyout--open'))
  }

  @HostListener('window:scroll', [])
  onScroll () {
    const element = this.winRef.document.documentElement
    if (element.scrollTop > 127) {
      this.winRef.document.body.classList.add('sticky')
    } else {
      this.winRef.document.body.classList.remove('sticky')
    }
  }

  close (event: Event, pushEvent: boolean): void {
    if (pushEvent) {
      this.sendGTMEvent(event)
    }
    this.selectedCategory$.next(undefined)
    this.closeContact()
    this.closeSearch()
    if (this.isMobile$.value && this.isMobileNavigationOpened$.value) {
      this.toggleMobileNavigation()
    }
  }

  ngOnDestroy (): void {
    this.subscription.unsubscribe()
  }

  getCategoryColor (category: NavigationNode): string {
    return category?.color || 'corporate-orange'
  }

  getSelectedCategoryColor (): string {
    return this.getSubCategoryByCategoryAndFilter()?.color || 'corporate-orange'
  }

  toggleContact (event: Event): void {
    if (this.showContact) {
      this.showContact = false
    } else {
      this.close(event, false)
      this.closeSearch()
      this.showContact = true
    }
  }

  closeContact (closeContact?: Event): void {
    if (closeContact && this.isMobileNavigationOpened$.value) {
      this.toggleMobileNavigation()
    }
    this.showContact = false
  }

  closeSearch (): void {
    this.showSearch = false
  }

  toggleSearch (): void {
    if (this.isMobileNavigationOpened$.value) {
      this.toggleMobileNavigation()
    }
    this.selectedCategory$.next(undefined)
    this.closeContact()
    this.showSearch = !this.showSearch
  }

  showBackdrop (): boolean {
    return this.isMobile$.value
      ? this.isMobileNavigationOpened$.value || this.showSearch
      : !!this.selectedCategory$.value || this.showSearch || this.showContact
  }

  sendGTMEvent (event: Event) {
    let target = (event?.target ?? {}) as HTMLElement
    if (!(target instanceof HTMLAnchorElement)) {
      target = target?.parentElement ?? {} as HTMLElement
    }
    const eventAction = target.getAttribute('data-event-action') ?? ''
    const eventLabel = target.getAttribute('data-event-label') ?? ''
    const eventCategory = target.getAttribute('data-event-category') ?? ''
    const eventFilter = target.getAttribute('data-filter')
    this.events.dispatch(new ClickEvent(eventCategory, eventAction, eventLabel, eventFilter))
  }

  private isSubCategorySelected (category: NavigationNode | undefined, activeFilter: NavigationNode | undefined,
                                 subCategory: NavigationNode | null): boolean {
    const label: any = category?.label ?? ''
    return activeFilter && category && category?.children?.some(child => child.id === activeFilter.id)
      ? !!subCategory
      : !!this.selectedSubCategories$.value?.[label]
  }

  readonly products = MbConstants.PRODUCTS
  readonly applications = MbConstants.APPLICATIONS
  readonly orange = MbConstants.CORPORATE_ORANGE
}

export interface MbCategoryNavigationState {
  selectedCategory: NavigationNode | undefined;
  selectedSubCategories: SelectedSubCategories;
  selectedFilter: SelectedFilter;
  navigation: NavigationNode[];
  searchClose: boolean;
  activeFilter: NavigationNode | undefined;
  activeSubCategory: NavigationNode | null;
  isMobile: boolean;
  isMobileNavigationOpened: boolean;
  isSubCategorySelected: boolean;
  dataFiltered: boolean;
}
